import {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { api } from "@/lib/api";

const TranslationContext = createContext(null);
export const useI18n = () => useContext(TranslationContext);

const LOCALE_KEY = "erio_locale";
const CACHE_KEY = "erio_tx_cache";

export const LANGUAGES = [
  { code: "en", label: "English", flag: "EN" },
  { code: "fr", label: "Français", flag: "FR" },
  { code: "de", label: "Deutsch", flag: "DE" },
  { code: "es", label: "Español", flag: "ES" },
  { code: "zh-CN", label: "简体中文", flag: "中" },
];

function loadCache() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY)) || {};
  } catch {
    return {};
  }
}

export const TranslationProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => localStorage.getItem(LOCALE_KEY) || "en");
  const [showOriginal, setShowOriginal] = useState(false);
  const [, forceRender] = useState(0);

  const cacheRef = useRef(loadCache());
  const pendingRef = useRef(new Set());
  const inflightRef = useRef(false);
  const timerRef = useRef(null);

  const persist = () => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheRef.current));
    } catch {
      /* quota — ignore */
    }
  };

  const flush = useCallback(async () => {
    if (inflightRef.current || pendingRef.current.size === 0 || locale === "en") return;
    const target = locale;
    const texts = Array.from(pendingRef.current).slice(0, 200);
    pendingRef.current = new Set(Array.from(pendingRef.current).slice(200));
    inflightRef.current = true;
    try {
      const { data } = await api.post("/translate", { target_lang: target, texts });
      cacheRef.current[target] = { ...(cacheRef.current[target] || {}), ...data.translations };
      persist();
      forceRender((v) => v + 1);
    } catch {
      /* leave originals on failure */
    } finally {
      inflightRef.current = false;
      if (pendingRef.current.size > 0) scheduleFlush();
    }
  }, [locale]);

  const scheduleFlush = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 140);
  }, [flush]);

  // Translate any English string -> active locale (cache-backed, debounced batch).
  const tx = useCallback(
    (text) => {
      if (!text || typeof text !== "string" || locale === "en" || showOriginal) return text;
      const c = cacheRef.current[locale];
      if (c && text in c) return c[text];
      if (!pendingRef.current.has(text)) {
        pendingRef.current.add(text);
        scheduleFlush();
      }
      return text;
    },
    [locale, showOriginal, scheduleFlush]
  );

  const setLocale = useCallback(async (code) => {
    setLocaleState(code);
    localStorage.setItem(LOCALE_KEY, code);
    forceRender((v) => v + 1);
    if (localStorage.getItem("erio_token")) {
      try {
        await api.put("/auth/locale", { locale: code });
      } catch {
        /* not fatal */
      }
    }
  }, []);

  // Sync from the authenticated user's saved locale on first load.
  useEffect(() => {
    if (!localStorage.getItem("erio_token")) return;
    api
      .get("/auth/me")
      .then(({ data }) => {
        if (data.locale && data.locale !== locale) {
          setLocaleState(data.locale);
          localStorage.setItem(LOCALE_KEY, data.locale);
          forceRender((v) => v + 1);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <TranslationContext.Provider
      value={{ locale, setLocale, tx, showOriginal, setShowOriginal, languages: LANGUAGES }}
    >
      {children}
    </TranslationContext.Provider>
  );
};
